import { __assign } from "tslib";
import { get, extend, merge, trimEnd, pickBy, noop } from 'lodash';
import { normalizeUrl } from './uri';
var DEFAULT_TIMEOUT = 12000;
var RETRY_TIMEOUT = 2500;
export default function createRequest(_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.apiBaseUrl, apiBaseUrl = _c === void 0 ? '' : _c, _d = _b.instance, instance = _d === void 0 ? '' : _d, _e = _b.getInstance, getInstance = _e === void 0 ? function () { return ''; } : _e, instanceId = _b.instanceId, _f = _b.locale, locale = _f === void 0 ? 'en' : _f, cookie = _b.cookie, _g = _b.timeout, timeout = _g === void 0 ? DEFAULT_TIMEOUT : _g, _h = _b.performanceTracker, performanceTracker = _h === void 0 ? { trackStart: noop, trackEnd: noop, parseHeader: noop } : _h, _j = _b.fetchFunction, fetchFunction = _j === void 0 ? undefined : _j, _k = _b.additionalHeaders, additionalHeaders = _k === void 0 ? {} : _k;
    var request = function (path, config) {
        if (config === void 0) { config = {}; }
        var url = normalizeUrl("" + trimEnd(config.apiBaseUrl || apiBaseUrl, '/') + path);
        var shouldParseHeaders = config.parseHeaders;
        var retryCount = config.retry;
        delete config.retry;
        delete config.parseHeaders;
        instance = instance || getInstance();
        var headers = pickBy(__assign(__assign(__assign({}, config.headers), { instance: instance,
            instanceId: instanceId, Authorization: instance, locale: locale,
            cookie: cookie }), additionalHeaders));
        extend(config, {
            headers: headers,
            timeout: timeout,
            credentials: get(config, 'credentials', 'same-origin'),
        }, request.meteredPathKey ? { meteredPathKey: request.meteredPathKey } : {});
        var marker = performanceTracker.trackStart((config.method || 'GET') + " " + path + " " + Date.now() % 10000);
        var start = function () { return (fetchFunction ? fetchFunction(url, config) : fetch(url, config)); };
        var parse = function (res) {
            return Promise.resolve(res)
                .then(handleSuccess)
                .then(function (response) {
                performanceTracker.trackEnd(marker, performanceTracker.parseHeader(response));
                return response;
            })
                .then(shouldParseHeaders ? parseHeadersAndBody : parseBody);
        };
        return retry(start, url, retryCount, timeout).then(parse);
    };
    request.post = function (path, data, config) {
        return request(path, merge({}, config, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }));
    };
    request.put = function (path, data, config) {
        return request(path, merge({}, config, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }));
    };
    request.patch = function (path, data, config) {
        return request(path, merge({}, config, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }));
    };
    request.delete = function (path, config) {
        return request(path, extend({}, config, {
            method: 'DELETE',
        }));
    };
    request.metered = function (meteredPathKey) {
        return Object.assign(request, { meteredPathKey: meteredPathKey });
    };
    return request;
}
function handleSuccess(response) {
    var status = response.status;
    if (status < 400) {
        return response;
    }
    return Promise.reject(response);
}
function parseBody(response) {
    return response.json().catch(function () { return Promise.resolve(); });
}
function parseHeadersAndBody(response) {
    return response
        .json()
        .then(function (data) { return ({
        body: data,
        headers: response.headers,
    }); })
        .catch(function () { return Promise.resolve(); });
}
export var timeoutPromise = function (promise, ms, errMsg) {
    var abort = get(promise, 'abort', noop);
    promise = get(promise, 'ready', promise);
    return new Promise(function (resolve, reject) {
        setTimeout(function () {
            abort();
            reject(new Error(errMsg));
        }, ms);
        promise.then(resolve, reject);
    });
};
export var retry = function (fn, url, retries, timeout) {
    if (retries === void 0) { retries = 1; }
    if (timeout === void 0) { timeout = RETRY_TIMEOUT; }
    return new Promise(function (resolve, reject) {
        return timeoutPromise(fn(), timeout, "Fetch timed out after " + timeout + "ms at url: " + url).then(function (response) {
            if (response.status >= 500 && retries > 0) {
                return retry(fn, url, retries - 1, timeout).then(resolve, reject);
            }
            resolve(response);
        }, function (error) {
            if (retries > 0) {
                return retry(fn, url, retries - 1, timeout).then(resolve, reject);
            }
            reject(error);
        });
    });
};
